import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CampaignProvider } from './contexts/CampaignContext';
import { MapProvider } from './contexts/MapContext';
import { ToastProvider } from './contexts/ToastContext';
import ToastViewport from './components/ui/ToastViewport';
import ToastEvents from './components/ui/ToastEvents';
import RequireAuth from './routes/RequireAuth';
import Login from './routes/Login';
import DMView from './routes/DMView';
import PlayerView from './routes/PlayerView';
import DisplayView from './routes/DisplayView';
import ErrorBoundary from './components/ErrorBoundary';
import { useWakeLock } from './lib/useWakeLock';
import './styles/main.css';

const CAMPAIGN_ID = import.meta.env.VITE_CAMPAIGN_ID || 'default-campaign';

export default function App() {
  // Keep device awake for the whole session, regardless of which view is active.
  useWakeLock(true);

  // iOS Safari composites backdrop-filter + mix-blend-mode on the CPU side
  // and tanks on older iPhones. Tag <html> so main.css can drop those
  // effects on iOS without affecting other devices.
  useEffect(() => {
    const ua = navigator.userAgent || '';
    const isIOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS reports as Mac
    if (isIOS) document.documentElement.classList.add('is-ios');
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ToastProvider>
      <AuthProvider>
        <CampaignProvider>
          <MapProvider>
          <ToastEvents />
          <ToastViewport />
          <Routes>
            {/* Root — redirect to DM login for the default campaign */}
            <Route
              path="/"
              element={<Navigate to={`/session/${CAMPAIGN_ID}/dm/login`} replace />}
            />

            {/* ── DM routes ───────────────────────────────────────── */}
            <Route path="/session/:id/dm/login" element={<Login role="dm" />} />
            <Route
              path="/session/:id/dm"
              element={
                <RequireAuth role="dm">
                  <DMView />
                </RequireAuth>
              }
            />

            {/* ── Player routes ────────────────────────────────────── */}
            <Route path="/session/:id/player/login" element={<Login role="player" />} />
            <Route
              path="/session/:id/player"
              element={
                <RequireAuth role="player">
                  <PlayerView />
                </RequireAuth>
              }
            />

            {/* ── Display view (no auth required) ─────────────────── */}
            <Route
              path="/session/:id/display"
              element={
                <RequireAuth role="display">
                  <DisplayView />
                </RequireAuth>
              }
            />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </MapProvider>
        </CampaignProvider>
      </AuthProvider>
      </ToastProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
